import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import customerService from '../services/customerService';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '../styles/phone-input.scss';

const AddressModal = ({ show, onHide, onSave, existingAddress, addressType = 'shipping' }) => {
    const { user, isAuthenticated } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        company: '',
        country: 'Türkiye',
        addressLine: '',
        buildingDetails: '',
        city: '',
        district: '',
        postCode: '',
        phone: user?.phoneNumber || '',
        email: user?.email || '',
        identityNumber: '',
        isDefault: false,
        addressType: addressType === 'shipping' ? 1 : 2, // 1: Shipping, 2: Billing
        saveAsBoth: true // Hem teslimat hem fatura adresi olarak kaydet
    });

    useEffect(() => {
        if (show) {
            if (!existingAddress) {
                setFormData(prev => ({
                    ...prev,
                    addressLine: '',
                    buildingDetails: '',
                    city: '',
                    district: '',
                    identityNumber: user?.identityNumber || ''
                }));
            }
        }
    }, [show, user, existingAddress]);

    useEffect(() => {
        if (existingAddress) {
            const addressParts = (existingAddress.address || '').split(',');
            let addressLine = '';
            let buildingDetails = '';
            
            if (addressParts.length > 0) {
                addressLine = addressParts[0].trim();
                if (addressParts.length > 1 && addressParts[1].trim().toLowerCase().includes('mahalle')) {
                    addressLine = addressParts[1].trim() + ', ' + addressLine;
                }
            }
            
            const buildingParts = addressParts.filter((part, index) => {
                const trimPart = part.trim().toLowerCase();
                return index > 0 && (
                    !trimPart.includes('mahalle') && 
                    (trimPart.includes('site') || 
                     trimPart.includes('bina') || 
                     trimPart.includes('apt') || 
                     trimPart.includes('kat') || 
                     trimPart.includes('daire'))
                );
            });
            
            if (buildingParts.length > 0) {
                buildingDetails = buildingParts.map(p => p.trim()).join(', ');
            }

            setFormData({
                firstName: existingAddress.firstName || user?.firstName || '',
                lastName: existingAddress.lastName || user?.lastName || '',
                company: existingAddress.company || '',
                country: existingAddress.country || 'Türkiye',
                addressLine: addressLine,
                buildingDetails: buildingDetails,
                city: existingAddress.province || existingAddress.city || '',
                district: existingAddress.district || '',
                postCode: existingAddress.postalCode || existingAddress.postCode || '',
                phone: existingAddress.phone || user?.phoneNumber || '',
                email: existingAddress.email || user?.email || '',
                identityNumber: existingAddress.identityNumber || user?.identityNumber || '',
                isDefault: existingAddress.isDefault || false,
                addressType: addressType === 'shipping' ? 1 : 2,
                saveAsBoth: true
            });
        } else {
            const fetchProfileData = async () => {
                try {
                    if (!isAuthenticated()) {
                        setFormData(prev => ({
                            ...prev,
                            firstName: '',
                            lastName: '',
                            company: '',
                            country: 'Türkiye',
                            addressLine: '',
                            buildingDetails: '',
                            city: '',
                            district: '',
                            postCode: '',
                            phone: '',
                            email: '',
                            identityNumber: '',
                            isDefault: false,
                            addressType: addressType === 'shipping' ? 1 : 2,
                            saveAsBoth: true
                        }));
                        return;
                    }
                    
                    setLoading(true);
                    setFormData(prev => ({
                        ...prev,
                        firstName: user?.firstName || '',
                        lastName: user?.lastName || '',
                        phone: user?.phoneNumber || '',
                        email: user?.email || '',
                        identityNumber: user?.identityNumber || '',
                        company: '',
                        country: 'Türkiye',
                        addressLine: '',
                        buildingDetails: '',
                        city: '',
                        district: '',
                        postCode: '',
                        addressType: addressType === 'shipping' ? 1 : 2,
                        isDefault: false,
                        saveAsBoth: true
                    }));
                } catch (error) {
                    console.error('Profil bilgileri yüklenirken hata:', error);
                } finally {
                    setLoading(false);
                }
            };

            fetchProfileData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, existingAddress, isAuthenticated, user, addressType]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePhoneChange = (value) => {
        setFormData(prev => ({
            ...prev,
            phone: value
        }));
    };

    const validateEmail = (email) => {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.firstName || !formData.lastName || !formData.addressLine || 
            !formData.city || !formData.district || !formData.postCode || 
            !formData.phone || !formData.email || !formData.identityNumber) {
            toast.error('Lütfen tüm zorunlu alanları doldurun. TC Kimlik Numarası da gereklidir.');
            return;
        }

        if (formData.identityNumber.length !== 11 || !/^\d+$/.test(formData.identityNumber)) {
            toast.error('Lütfen geçerli bir TC Kimlik Numarası girin (11 hane, sadece rakam).');
            return;
        }

        if (!validateEmail(formData.email)) {
            toast.error('Lütfen geçerli bir e-posta adresi girin.');
            return;
        }
        
        try {
            setLoading(true);
            
            const fullAddress = formData.addressLine + 
                (formData.buildingDetails ? ', ' + formData.buildingDetails : '');
            
            const addressData = {
                fullName: `${formData.firstName} ${formData.lastName}`,
                phone: formData.phone,
                address: fullAddress,
                province: formData.city,
                district: formData.district,
                postalCode: formData.postCode,
                identityNumber: formData.identityNumber,
                addressType: formData.addressType,
                isDefault: formData.isDefault
            };
            
            if (isAuthenticated()) {
                if (existingAddress?.id) {
                    await customerService.updateCustomerAddress(existingAddress.id, addressData);
                } else {
                    await customerService.addCustomerAddress(addressData);
                    
                    if (formData.saveAsBoth) {
                        const otherAddressData = {
                            ...addressData,
                            addressType: formData.addressType === 1 ? 2 : 1
                        };
                        
                        await customerService.addCustomerAddress(otherAddressData);
                    }
                }
            }
            
            onSave({
                ...addressData,
                id: existingAddress?.id || undefined,
                saveAsBoth: existingAddress?.id ? false : formData.saveAsBoth
            });
            
            onHide();
            
        } catch (error) {
            console.error('Adres kaydetme hatası:', error);
            toast.error('Adres kaydedilirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal 
            show={show} 
            onHide={onHide}
            size="lg"
            centered
            backdrop="static"
            className="address-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold">Adres Bilgileri</Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-2">
                <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                        <div className="col-12 mb-2">
                            <h5 className="text-muted">Kişisel Bilgiler</h5>
                            <hr className="mt-1 mb-3" />
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <label htmlFor="firstName" className="form-label mb-1 small fw-medium">Adınız</label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                className="form-control form-control-lg"
                                placeholder="Adınızı girin"
                                required
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label htmlFor="lastName" className="form-label mb-1 small fw-medium">Soyadınız</label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                className="form-control form-control-lg"
                                placeholder="Soyadınızı girin"
                                required
                            />
                        </div>
                        <div className="col-12 mb-3">
                            <label htmlFor="company" className="form-label mb-1 small fw-medium">
                                Şirket Adı <span className="text-muted fs-7">(isteğe bağlı)</span>
                            </label>
                            <input
                                type="text"
                                id="company"
                                name="company"
                                value={formData.company}
                                onChange={handleInputChange}
                                className="form-control form-control-lg"
                                placeholder="Şirket adınız (doldurmak zorunda değilsiniz)"
                            />
                        </div>
                        
                        <div className="col-12 mb-2">
                            <h5 className="text-muted">Adres Bilgileri</h5>
                            <hr className="mt-1 mb-3" />
                        </div>
                        
                        <div className="col-12 mb-3">
                            <label htmlFor="country" className="form-label mb-1 small fw-medium">Ülke</label>
                            <input
                                type="text"
                                id="country"
                                name="country"
                                value={formData.country}
                                onChange={handleInputChange}
                                className="form-control form-control-lg"
                                placeholder="Türkiye"
                                required
                                readOnly
                            />
                        </div>
                        
                        <div className="col-12 mb-3">
                            <label htmlFor="addressLine" className="form-label mb-1 small fw-medium">Mahalle, Sokak / Cadde</label>
                            <input
                                type="text"
                                id="addressLine"
                                name="addressLine"
                                value={formData.addressLine}
                                onChange={handleInputChange}
                                className="form-control form-control-lg"
                                placeholder="Atatürk Mahallesi, Cumhuriyet Caddesi No: 123"
                                required
                            />
                        </div>
                        
                        <div className="col-12 mb-3">
                            <label htmlFor="buildingDetails" className="form-label mb-1 small fw-medium">
                                Bina/Site Adı, Kat, Daire <span className="text-muted fs-7">(isteğe bağlı)</span>
                            </label>
                            <input
                                type="text"
                                id="buildingDetails"
                                name="buildingDetails"
                                value={formData.buildingDetails}
                                onChange={handleInputChange}
                                className="form-control form-control-lg"
                                placeholder="Yeşil Vadi Sitesi, A Blok, Kat: 5, Daire: 42"
                            />
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <label htmlFor="city" className="form-label mb-1 small fw-medium">İl</label>
                            <input
                                type="text"
                                id="city"
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                                className="form-control form-control-lg"
                                placeholder="İstanbul"
                                required
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label htmlFor="district" className="form-label mb-1 small fw-medium">İlçe</label>
                            <input
                                type="text"
                                id="district"
                                name="district"
                                value={formData.district}
                                onChange={handleInputChange}
                                className="form-control form-control-lg"
                                placeholder="Kadıköy"
                                required
                            />
                        </div>
                        
                        <div className="col-12 mb-2">
                            <h5 className="text-muted">İletişim Bilgileri</h5>
                            <hr className="mt-1 mb-3" />
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <label htmlFor="postCode" className="form-label mb-1 small fw-medium">Posta Kodu</label>
                            <input
                                type="text"
                                id="postCode"
                                name="postCode"
                                value={formData.postCode}
                                onChange={handleInputChange}
                                className="form-control form-control-lg"
                                placeholder="34000"
                                required
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label htmlFor="identityNumber" className="form-label mb-1 small fw-medium">TC Kimlik Numarası</label>
                            <input
                                type="text"
                                id="identityNumber"
                                name="identityNumber"
                                value={formData.identityNumber}
                                onChange={handleInputChange}
                                className="form-control form-control-lg"
                                placeholder="11 haneli rakamlar"
                                maxLength="11"
                                pattern="\d{11}"
                                title="TC Kimlik Numarası 11 haneli ve sadece rakamlardan oluşmalıdır."
                                required
                            />
                            <small className="form-text text-muted">Faturanız ve güvenli ödeme işleminiz için gereklidir.</small>
                        </div>
                        <div className="col-md-6 mb-3">
                            <label htmlFor="phone" className="form-label mb-1 small fw-medium">Telefon</label>
                            <div className="position-relative">
                              <PhoneInput
                                  country={'tr'}
                                  value={formData.phone}
                                  onChange={handlePhoneChange}
                                  inputClass="form-control form-control-lg"
                                  containerClass="phone-input-container"
                                  containerStyle={{
                                      width: '100%',
                                      marginBottom: '0',
                                      position: 'relative'
                                  }}
                                  inputStyle={{
                                      width: '100%',
                                      height: 'unset !important',
                                      fontSize: '14px',
                                      borderRadius: '5px',
                                      border: '1px solid var(--gray-100)',
                                      backgroundColor: 'transparent',
                                      paddingLeft: '52px'
                                  }}
                                  buttonStyle={{
                                      border: '1px solid var(--gray-100)',
                                      borderRight: 'none',
                                      backgroundColor: 'transparent',
                                      borderTopLeftRadius: '5px',
                                      borderBottomLeftRadius: '5px',
                                      position: 'absolute',
                                      top: '0',
                                      left: '0',
                                      zIndex: '10'
                                  }}
                                  placeholder="5XX XXX XX XX"
                                  dropdownStyle={{zIndex: '999'}}
                                  enableSearch={false}
                                  searchPlaceholder="Ülke ara..."
                                  countryCodeEditable={false}
                                  preferredCountries={['tr']}
                                  formatOnInit={true}
                                  autoFormat={true}
                                  disableDropdown={false}
                                  disableCountryCode={false}
                                  inputProps={{
                                      id: 'phone',
                                      name: 'phone',
                                      required: true
                                  }}
                              />
                            </div>
                            <small className="form-text text-muted">
                                Sadece numaraları girin, ülke kodu otomatik eklenir
                            </small>
                        </div>
                        <div className="col-12 mb-3">
                            <label htmlFor="email" className="form-label mb-1 small fw-medium">E-posta</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className="form-control form-control-lg"
                                placeholder="adiniz@example.com"
                                required
                                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                            />
                            <small className="form-text text-muted">
                                Geçerli bir e-posta adresi girin (adiniz@example.com)
                            </small>
                        </div>
                        
                        {/* Adres Tercihleri bölümü - sadece yeni adres eklerken göster */}
                        {!existingAddress && (
                            <>
                                <div className="col-12 mb-2">
                                    <h5 className="text-muted">Adres Tercihleri</h5>
                                    <hr className="mt-1 mb-3" />
                                </div>
                                
                                <div className="col-12 mb-3">
                                    <div className="form-check">
                                        <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            id="isDefault" 
                                            name="isDefault"
                                            checked={formData.isDefault}
                                            onChange={handleInputChange}
                                        />
                                        <label className="form-check-label" htmlFor="isDefault">
                                            Bu adresi varsayılan adresim yap
                                        </label>
                                        <div className="form-text text-muted small">
                                            Varsayılan adres, sonraki siparişlerinizde otomatik olarak seçilir
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 mb-4">
                                    <div className="form-check">
                                        <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            id="saveAsBoth" 
                                            name="saveAsBoth"
                                            checked={formData.saveAsBoth}
                                            onChange={handleInputChange}
                                        />
                                        <label className="form-check-label" htmlFor="saveAsBoth">
                                            Aynı adresi hem teslimat hem de fatura için kullan
                                        </label>
                                        <div className="form-text text-muted small">
                                            İşaretlerseniz, bu adres her iki adres türü için de kaydedilir (sadece yeni adres eklerken geçerli)
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        
                        <div className="col-12">
                            <button 
                                type="submit" 
                                className="btn w-100 payment-submit-btn" 
                                disabled={loading}
                                style={{
                                    backgroundColor: '#37B24D',
                                    borderRadius: '8px'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2f9940'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#37B24D'}
                            >
                                {loading ? (
                                    <span>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        İşleniyor...
                                    </span>
                                ) : (
                                    existingAddress ? 'Güncelle' : 'Kaydet'
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default AddressModal; 